/**
 * THE REVERTED-SLOT SET — R1, and the eviction that keeps it bounded.
 *
 * A revert is ROUTINE: 50–100 per hour, and more since SIMD-0286 raised the non-canonical share from ~0.2 %
 * to 0.3–2.0 %. It is NOT an error state for market data and must never toast the user. What it IS, is a
 * filter every seed row passes through: a seed read a millisecond before a revert legitimately contains rows
 * from the forked slot, and nothing else will ever tell the client to drop them.
 *
 * THE EVICTION IS EXACT, NOT A HEURISTIC. Everything below `head.finalizedSlot` is dropped because a
 * finalized slot CAN NEVER FORK — so a slot below the finalized frontier can never be the subject of a future
 * revert, and holding it buys nothing. The 256 cap is the belt: at ~2.5 slots/s and a ~32-slot maximum fork
 * depth, 256 is ~100 s of frontier, which is well beyond any window in which a slot can still be reverted.
 *
 * WHY A CAP AT ALL, GIVEN THE EXACT EVICTION: the eviction only runs when a `head` frame arrives. A socket
 * that is receiving reverts but no heads is a broken server, and an unbounded set on a long-lived page is how
 * a tab reaches a gigabyte. The cap makes the failure a bounded one.
 */

export const REVERTED_SLOTS_MAX = 256;

export class RevertedSlots {
  private readonly slots = new Set<number>();

  add(slot: number): void {
    this.slots.add(slot);
    if (this.slots.size > REVERTED_SLOTS_MAX) {
      // Drop the LOWEST, not the oldest-inserted. They coincide in practice (the frontier is monotone) but
      // "lowest" is the property that matters: the lowest slot is the one closest to finality and therefore
      // the one least likely to still be needed.
      let min = Number.POSITIVE_INFINITY;
      for (const s of this.slots) if (s < min) min = s;
      this.slots.delete(min);
    }
  }

  has(slot: number): boolean {
    return this.slots.has(slot);
  }

  /** Called on every `status/head`. */
  evictBelow(finalizedSlot: number): void {
    for (const s of this.slots) if (s < finalizedSlot) this.slots.delete(s);
  }

  get size(): number {
    return this.slots.size;
  }

  /** Test-only readout. Sorted so an assertion does not depend on insertion order. */
  snapshot(): number[] {
    return [...this.slots].sort((a, b) => a - b);
  }

  clear(): void {
    this.slots.clear();
  }
}
